import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { publicCollection } from '../lib/publicContent';
import { optimizeDataImages } from '../lib/imageCompression';
import { SITE_PHOTOS } from '../sitePhotos';

interface Post {
  id: string;
  title: string;
  slug: string;
  date: string;
  excerpt: string;
  image: string;
  status: string;
  content?: string;
  publishAt?: string;
  categoryId?: string;
  categorySlug?: string;
  createdAt?: { seconds: number; nanoseconds: number } | Date | string;
}

interface NewsGridProps {
  categorySlug?: string;
  mobileRail?: boolean;
}

export default function NewsGrid({ categorySlug, mobileRail = false }: NewsGridProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const railRef = useRef<HTMLDivElement>(null);
  const [firestoreNews, setFirestoreNews] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 9;

  useEffect(() => {
    const fetchFirestoreNews = async () => {
      try {
        const posts = optimizeDataImages(await publicCollection('posts')) as Post[];
        setFirestoreNews(posts);
      } catch (error) {
        console.error('Error fetching firestore news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFirestoreNews();
  }, []);

  // Helper function to get timestamp from createdAt field (Firestore timestamp or Date)
  const getCreatedAtTimestamp = (item: Post): number => {
    if (!item.createdAt) return 0;
    if (typeof item.createdAt === 'object' && 'seconds' in item.createdAt) {
      // Firestore timestamp format
      return item.createdAt.seconds * 1000;
    }
    if (item.createdAt instanceof Date) {
      return item.createdAt.getTime();
    }
    return new Date(item.createdAt).getTime();
  };

  // Merge or filter based on categorySlug
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' });
  const allNews = firestoreNews
    .filter(item => !categorySlug || item.categorySlug === categorySlug)
    .filter(item => item.publishAt ? Date.parse(item.publishAt) <= Date.now() : item.date <= today)
    .sort((a,b)=>Date.parse(b.date)-Date.parse(a.date) || getCreatedAtTimestamp(b)-getCreatedAtTimestamp(a));

  const totalPages = Math.ceil(allNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentNews = allNews.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    railRef.current?.scrollTo({ left: 0, behavior: 'instant' });
    // Scroll to the top of the news section smoothly
    const element = document.getElementById('news-grid-container');
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  if (allNews.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg font-medium">Chưa có bài viết nào trong chuyên mục này.</p>
        <p className="text-sm mt-1">Hãy quay lại sau hoặc xem các bài viết khác.</p>
      </div>
    );
  }

  return (
    <div id="news-grid-container">
      <div ref={railRef} className={`${mobileRail ? "mobile-card-rail" : ""} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`} tabIndex={mobileRail ? 0 : undefined}>
        {currentNews.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200/70 hover:shadow-lg transition-shadow group flex flex-col">
            <Link to={`/tin-tuc/${item.slug}`} className="block overflow-hidden aspect-video bg-slate-100">
              <img 
                src={item.image || (item.content ? (item.content.match(/<img[^>]+src="([^">]+)"/) || [])[1] : undefined) || SITE_PHOTOS[0].url}
                alt={item.title} 
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = SITE_PHOTOS[0].url;
                }}
              />
            </Link>
            <div className="p-4 flex flex-col flex-grow">
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Calendar size={14} className="mr-1" />
                <span>{item.date}</span>
              </div>
              <h3 className="font-bold text-sm md:text-base mb-2 line-clamp-2 uppercase">
                <Link to={`/tin-tuc/${item.slug}`} className="hover:text-primary transition-colors">
                  {item.title}
                </Link>
              </h3>
              <p className="text-gray-600 text-sm line-clamp-3 flex-grow">
                {item.excerpt}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-10 space-x-2">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-md border text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="flex space-x-1">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`w-10 h-10 rounded-md border flex items-center justify-center font-medium transition-colors cursor-pointer ${
                  currentPage === i + 1 
                    ? 'bg-primary text-white border-primary cursor-default'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-md border text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            aria-label="Next page"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
